import { useCallback, useContext, useEffect, useState } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { Input } from '@get-mosh/msh-shared';
import classNames from 'classnames';
import { isMosh, isMoshy } from 'src/env.mjs';

const QuizFirstLastName = () => {
    const { currentQuestion, handleSubmitAnswer, state } =
        useContext(QuizContext);
    const {
        questionId,
        questionText,
        questionExplanation,
        answerList,
        userAnswers,
    } = currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [, setContinueVisible] = state.continueVisible;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [firstName, setFirstName] = useState('');
    const [firstNameTouched, setFirstNameTouched] = useState(false);
    const [lastName, setLastName] = useState('');
    const [lastNameTouched, setLastNameTouched] = useState(false);

    // Set existing answer
    useEffect(() => {
        let newFirstName: string | null = null;
        let newLastName: string | null = null;
        if (userAnswers.length === 2) {
            newFirstName = userAnswers[0].answerText;
            newLastName = userAnswers[1].answerText;
        }
        if (newFirstName) setFirstName(newFirstName);
        if (newLastName) setLastName(newLastName);
    }, [userAnswers]);

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (!continueDisabled && questionId) {
            handleSubmitAnswer({
                questionId: questionId,
                questionText: questionText,
                answerIds: [answerList[0].answerId, answerList[1].answerId],
                answerTexts: [firstName, lastName],
            });
        }
    }, [
        answerList,
        firstName,
        lastName,
        continueDisabled,
        questionId,
        questionText,
        handleSubmitAnswer,
    ]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);

    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueVisible(true);
            setContinueDisabled(!(firstName && lastName));
        }
    }, [
        firstName,
        lastName,
        pageTransitioning,
        setContinueDisabled,
        setContinueVisible,
    ]);

    return (
        <div className="flex items-center justify-center">
            <div className="flex max-w-[300px] flex-col sm:max-w-[400px]">
                <div className="flex flex-col space-y-2 pb-4">
                    <h4 className="text-left">{questionText}</h4>
                    {questionExplanation ? (
                        <p
                            className={classNames({
                                'text-green-faint-dark': isMosh,
                                'text-grey-400': isMoshy,
                            })}
                        >
                            {questionExplanation}
                        </p>
                    ) : null}
                </div>
                <Input
                    data-testid="first-name-input"
                    value={firstName || ''}
                    onChange={(e) => setFirstName(e.target.value)}
                    label="First name"
                    required
                    placeholder="eg. John"
                    type={'text'}
                    onFocus={() =>
                        firstNameTouched && setFirstNameTouched(false)
                    }
                    showFeedback={true}
                    className={classNames({
                        'text-green-dark': isMosh,
                        'text-grey-400': isMoshy,
                    })}
                />
                <Input
                    data-testid="last-name-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    label="Last name"
                    required
                    placeholder="eg. Smith"
                    type={'text'}
                    onFocus={() => lastNameTouched && setLastNameTouched(false)}
                    showFeedback={true}
                    className={classNames({
                        'text-green-dark': isMosh,
                        'text-grey-400': isMoshy,
                    })}
                />
            </div>
        </div>
    );
};

export default QuizFirstLastName;
